import { apiv1 as api } from '@/adapters';
import { extractSensitiveContent } from '@/adapters/shared';
import {
  SENSITIVE_CONTENT_LIST_LIMIT,
  type SensitiveContent,
  type SensitiveContentList,
  type SensitiveContentValue,
} from '@/types/core/sensitive-content';
import type { UUID } from '@/utils/uuid';

import {
  TRANSIENT_CONFLICT,
  problemDetails,
  problemMessage,
} from '@/services/problem-details';

/** The server refused a promote (409 `not_promotable`); the message is its reason. */
export class NotPromotableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotPromotableError';
  }
}

export const sensitiveContentService = {
  /** List the tenant's live rows, newest first; expired rows are omitted by the API. */
  list: async (tenantId: UUID): Promise<SensitiveContentList> => {
    const res = await api.listSensitiveContent(tenantId, {
      limit: SENSITIVE_CONTENT_LIST_LIMIT,
    });
    if (!res.ok) {
      throw new Error(
        problemMessage(
          res.error,
          `Failed to load sensitive content (${String(res.status)})`
        )
      );
    }
    return {
      items: (res.data?.models ?? []).map(extractSensitiveContent),
      truncated: res.data?.truncated ?? false,
    };
  },

  /**
   * Reveal one row's value; the server audits every call. Returns null when the
   * row is unknown, expired or removed.
   */
  getValue: async (
    tenantId: UUID,
    connectorId: UUID,
    id: string
  ): Promise<SensitiveContentValue | null> => {
    const res = await api.getSensitiveContentValue(tenantId, connectorId, id);
    if (res.status === 404) return null;
    if (
      res.status === 409 &&
      problemDetails(res.error).title === TRANSIENT_CONFLICT
    ) {
      throw new Error('The reveal could not be recorded. Try again.');
    }
    if (!res.ok) {
      throw new Error(
        problemMessage(
          res.error,
          `Failed to reveal sensitive content (${String(res.status)})`
        )
      );
    }
    return { value: res.data?.value ?? null };
  },

  /** One row's metadata; null when it is unknown, expired or removed. */
  get: async (
    tenantId: UUID,
    connectorId: UUID,
    id: string
  ): Promise<SensitiveContent | null> => {
    const res = await api.getSensitiveContent(tenantId, connectorId, id);
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new Error(
        problemMessage(
          res.error,
          `Failed to load sensitive content (${String(res.status)})`
        )
      );
    }
    return extractSensitiveContent(res.data ?? {});
  },

  /**
   * Promote a template-shaped row into a secret named `name`; the server removes
   * the row. Returns the new secret's ID, or null when the row is gone. A 409
   * not_promotable throws NotPromotableError carrying the server's reason; a
   * 409 transient_conflict throws a plain error asking for a retry.
   */
  promote: async (
    tenantId: UUID,
    connectorId: UUID,
    id: string,
    name: string
  ): Promise<UUID | null> => {
    const res = await api.promoteSensitiveContent(tenantId, connectorId, id, {
      name,
    });
    if (res.status === 404) return null;
    if (res.status === 409) {
      const problem = problemDetails(res.error);
      if (problem.title === TRANSIENT_CONFLICT) {
        throw new Error('The value changed at the same time. Try again.');
      }
      throw new NotPromotableError(
        problem.detail || 'This value cannot be promoted.'
      );
    }
    if (!res.ok) {
      throw new Error(
        problemMessage(
          res.error,
          `Failed to promote sensitive content (${String(res.status)})`
        )
      );
    }
    return (res.data?.secret_id ?? '') as UUID;
  },
};
